import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import FsLightbox from 'fslightbox-react';
import { useEffect, useRef, useState } from 'react';
import Carousel from 'react-material-ui-carousel';

import BookingConfirmationDetailCard from 'reservations/confirmation/components/BookingConfirmationDetailCard';
import { useInstagramMediaQuery } from 'reservations/preDining/queries/useInstagramMediaQuery';

type Props = {
  context: {
    follow_button: boolean;
  };
};

const FollowButton = ({
  instagramName
}: {
  instagramName: string | undefined;
}) => (
  <Button
    variant="secondary"
    href={`https://instagram.com/${instagramName}`}
    target="_blank"
  >
    <Typography variant="h3">FOLLOW</Typography>
  </Button>
);

const InstagramModule = ({ context }: Props) => {
  const { follow_button } = context;
  const { data } = useInstagramMediaQuery();
  const [toggler, setToggler] = useState(false);
  const [image, setImage] = useState<string>('');
  const imageRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  // istanbul ignore next
  const updateHeight = () => {
    const width = imageRef.current?.offsetWidth;
    width && setHeight((4 / 3) * width);
  };

  useEffect(() => {
    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => window.removeEventListener('resize', updateHeight);
  }, [imageRef]);

  const openModal = (image: string) => {
    setToggler(!toggler);
    setImage(image);
  };

  // connected = false comes from fetchInstagramMedia when the api call returns an error
  // When using `!data?.connected` for the conditional, there's a bug making the slides to not show
  if (data?.connected == false) {
    return <div data-testid="instagram-not-connected"></div>;
  }

  return (
    <BookingConfirmationDetailCard
      header="Follow Us"
      endAdornment={
        follow_button && <FollowButton instagramName={data?.instagramName} />
      }
    >
      <FsLightbox
        data-testid="lightbox"
        toggler={toggler}
        sources={[image]}
        disableThumbs={true}
        disableLocalStorage={true}
        disableSlideSwiping={true}
        types={['image']}
        customAttributes={[{ 'data-testid': 'instagram-image' }]}
      />
      <Link
        href={`https://instagram.com/${data?.instagramName}`}
        target="_blank"
        sx={{
          textDecoration: 'none',
          textTransform: 'uppercase'
        }}
      >
        @{data?.instagramName}
      </Link>
      <div ref={imageRef}>
        <Carousel
          animation="slide"
          indicators={false}
          navButtonsAlwaysVisible={true}
          sx={{
            width: '100%',
            height: `${height}px`
          }}
        >
          {data?.media.map((item, index) => (
            <Item
              key={index}
              src={item.mediaUrl}
              height={height}
              openModal={openModal}
            />
          ))}
        </Carousel>
      </div>
    </BookingConfirmationDetailCard>
  );
};

type ItemProps = {
  src: string;
  height: number;
  openModal: (name: string) => void;
};

const Item = ({ src, openModal, height }: ItemProps) => (
  <div
    data-testid="instagram-carousel-container"
    onClick={() => openModal(src)}
    style={{
      width: '100%',
      height: `${height}px`,
      backgroundImage: `url(${src})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center'
    }}
  ></div>
);

export default InstagramModule;
